<template>
  <div class="operation text-center">
    <div v-if="!isDone">
      <div class="counter">
        <div class="counter-cur">{{ iProblem }}</div>
        <div class="counter-total">{{ this.myDrill.config.nProblems }}</div>
      </div>
      <div class="clearfix">
        <div class="score score-wrong">{{ nWrong }}</div>
        <div class="score score-correct">{{ nCorrect }}</div>
      </div>
      <br>
      <!-- vertical display -->
      <div class="d-inline-block text-right">
        <!-- show opreands -->
        <div class="d-inline-block operand-wide text-right">
          <br>{{ this.equation.operator }}</div>
        <div class="d-inline-block operand-wide text-right" style="margin-right: -0.1em;">
          <div>{{ this.equation.lhs1String().split(".")[0] }}</div>
          <div>{{ this.equation.lhs2String().split(".")[0] }}</div>
        </div>
        <div class="d-inline-block operand-wide text-center" style="margin-right: -0.1em;">
          <div v-if="!this.equation.lhs1.isInteger()">.</div>
          <div v-if="!this.equation.lhs2.isInteger()">.</div>
        </div>
        <div class="d-inline-block operand-wide text-left">
          <div>{{ this.equation.lhs1String().split(".")[1] }}</div>
          <div>{{ this.equation.lhs2String().split(".")[1] }}</div>
        </div>
        <div class="divider"></div>
        <!-- show input boxes, NOTE that col starts from 1 -->
        <div class="d-inline-block ml-1" style="border: 1px solid #AAA">
          <input v-for="col in inputWholePlaces" :key="col"
            type="number" class="operand text-center" style="width: 0.9em;"
            :id="'userInput' + col"
            :class="inputStatus"
            :disabled="!inputAllowed"
            :style="col === 1 ? 'border: none;' : 'border: none; border-left: 1px solid #DDD;'"
            v-model.number="userInput[col]"
            @keydown.enter.prevent="submitAnswer"
            @keydown.arrow-up.prevent="doNothing"
            @keydown.arrow-down.prevent="doNothing"
            @keydown.arrow-left.prevent="moveLeft(col)"
            @keydown.arrow-right.prevent="moveRight(col)"
            @input.prevent="handleInput($event, col)"/>
        </div>
        <div class="d-inline-block operand" style="width: 0.4em;">.</div>
        <div class="d-inline-block mr-1" style="border: 1px solid #AAA">
          <input v-for="col in inputDecPlaces" :key="inputWholePlaces + col"
            type="number" class="operand text-center" style="width: 0.9em;"
            :id="'userInput' + (inputWholePlaces + col)"
            :class="inputStatus"
            :disabled="!inputAllowed"
            :style="col === 1 ? 'border: none;' : 'border: none; border-left: 1px solid #DDD;'"
            v-model.number="userInput[inputWholePlaces + col]"
            @keydown.enter.prevent="submitAnswer"
            @keydown.arrow-up.prevent="doNothing"
            @keydown.arrow-down.prevent="doNothing"
            @keydown.arrow-left.prevent="moveLeft(inputWholePlaces + col)"
            @keydown.arrow-right.prevent="moveRight(inputWholePlaces + col)"
            @input.prevent="handleInput($event, inputWholePlaces + col)"/>
        </div>
      </div>
      <br>
      <br>
      <div class="message" v-for="index in messages.length" :key="index">
        {{ messages[index - 1] }}</div>
      <b-button ref="btnCheck" variant="info" class="mt-3"
        :disabled="!inputAllowed"
        :class="btnCheckStatus"
        @click.prevent="submitAnswer">Check</b-button>
      <b-button ref="btnGotit" variant="info" class="mt-3"
        :class="btnGotitStatus"
        @click.prevent="newProblem">Got it!</b-button>
      <p class="mt-3"><small>
        You can use the arrow keys to move around.<br />
        You can hit the enter key to check.
      </small></p>
    </div>
    <div v-else class="message">
      <br>
      <b-row>
        <b-col cols="6" class="text-right">Total:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ this.myDrill.config.nProblems }}</b> Problems</b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="text-right">Correct:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ nCorrect }}</b> Problems</b-col>
      </b-row>
      <b-row>
        <b-col cols="6" class="text-right">Incorrect:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ nWrong }}</b> Problems</b-col>
      </b-row>
      <b-row class="my-4">
        <b-col cols="6" class="text-right">Score:</b-col>
        <b-col cols="6" class="text-left p-0"><b>{{ getScore() }}</b> %</b-col>
      </b-row>
      <br>
    </div>
  </div>
</template>

<script>
import '@/common/css/operation.css'
import * as uiUtil from '@/common/js/uiutil.js'

// required config: nProblems
// optional config: maxWholePlaces, maxDecPlaces
export default {
  name: 'LongAddDecimal',
  props: [ 'myDrill' ],
  data () {
    return {
      // status
      iProblem: 0,
      nCorrect: 0,
      nWrong: 0,
      isDone: false,

      // problem
      equation: null,

      // user input & responses
      inputWholePlaces: null,
      inputDecPlaces: null,
      inputPlaces: null,
      userInput: null,
      inputAllowed: true,
      inputStatus: null,
      btnCheckStatus: null,
      btnGotitStatus: null,
      messages: [ null, null ]
    }
  },
  created () {
    this.newProblem()
  },
  watch: {
    isDone: function () {
      if (this.isDone) {
        this.$nextTick(() => this.$emit('completed', this.getScore()))
      }
    }
  },
  methods: {
    newProblem: function () {
      if (this.iProblem >= this.myDrill.config.nProblems) {
        this.isDone = true
        return false
      }

      // new problem
      this.iProblem = this.iProblem + 1
      this.equation = this.myDrill.equation()

      // initialize user input fields
      this.inputWholePlaces = this.myDrill.config.maxWholePlaces || this.equation.rhsMaxWholePlaces()
      this.inputDecPlaces = this.myDrill.config.maxDecPlaces || this.equation.rhsMaxDecPlaces()
      this.inputPlaces = this.inputWholePlaces + this.inputDecPlaces
      this.userInput = new Array(this.inputPlaces + 1).fill(null)
      this.inputAllowed = true
      this.inputStatus = null
      this.btnCheckStatus = 'd-inline'
      this.btnGotitStatus = 'd-none'

      // initial message
      this.messages[0] = this.myDrill.instruction
      for (var i = 1, len = this.messages.length; i < len; i++) {
        this.messages[i] = null
      }

      // set focus on input box if displayed
      var elementName = '#userInput' + this.inputPlaces
      this.$nextTick(() => this.$el.querySelector(elementName).focus())

      return true
    },
    submitAnswer: function () {
      if (uiUtil.isEmpty(this.userInput[this.inputWholePlaces])) {
        var elementName = '#userInput' + this.inputWholePlaces
        this.$nextTick(() => this.$el.querySelector(elementName).focus())
        return false
      }

      this.inputAllowed = false

      if (this.isAnswer()) {
        this.nCorrect = this.nCorrect + 1
        this.inputStatus = 'input-correct'
        this.messages[0] = uiUtil.msgCorrect()
        setTimeout(() => this.newProblem(), 1000)
      } else {
        this.nWrong = this.nWrong + 1
        this.inputStatus = 'input-wrong'
        this.btnCheckStatus = 'd-none'
        this.btnGotitStatus = 'd-inline'
        this.messages[0] = uiUtil.msgWrong()
        this.messages[1] = this.equation.toString()

        this.$nextTick(() => this.$refs.btnGotit.focus())
      }

      return true
    },
    moveLeft: function (col) {
      if (col <= 1) return

      var elementName = '#userInput' + (col - 1)
      this.$nextTick(() => this.$el.querySelector(elementName).focus())
    },
    moveRight: function (col) {
      if (col >= this.inputPlaces) return

      var elementName = '#userInput' + (col + 1)
      this.$nextTick(() => this.$el.querySelector(elementName).focus())
    },
    handleInput: function (event, col) {
      var isEdge = typeof event.data === 'undefined' // event.data is not defined on Edge
      var inputData = !isEdge ? event.data : event.target.value[event.target.value.length - 1]

      this.userInput[col] = inputData
      this.moveLeft(col)
    },
    doNothing: function (event) {
      // ignore the event
    },
    isAnswer: function () {
      try {
        var userAnswer = ''

        for (var i = 1; i <= this.inputPlaces; i++) {
          userAnswer += this.userInput[i] || '0'
          if (i === this.inputWholePlaces) userAnswer += '.'
        }
        return this.equation.rhsEquals(userAnswer)
      } catch (error) {
        return false
      }
    },
    getScore: function () {
      return Math.ceil(100 * this.nCorrect / this.myDrill.config.nProblems)
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/math/operations/LongAddDecimal.vue